import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import { authService } from '../service/auth.service';
import { useThrottle } from '../../hooks/useThrottle';
import { getPendingInvite, clearPendingInvite } from '../../invite';
import { HttpFactoryService, clearStoredToken } from '../../common/services';
import { BACKEND_KEYS, ROUTER_LINKS } from '../../common/consts/app-keys.const';

interface IAuthProviderProps {
  children: React.ReactNode;
}

const Context = React.createContext<{
  isAuthenticated: null | boolean;
  isLoading: boolean;
  user: {
    id: number;
    email: string;
    displayName: string | null;
    avatar: string | null;
    hasActiveSubscription: boolean;
    subscriptionTier: string | null;
    subscriptionExpiresAt: string | null;
    subscriptionCancelledAt: string | null;
    isProGifted: boolean;
    hasFreeTrialAbility: boolean;
    isPro: boolean;
    mfaEnabled: boolean;
    mfaEmailEnabled: boolean;
    mfaPushEnabled: boolean;
    isEnterprise: boolean;
    companyId: number | null;
    companySlug: string | null;
    companyName: string | null;
    companyRole: string | null;
  } | null;
  isAdmin: boolean;
  setIsAuthenticated: (value: null | boolean) => void;
  checkAuth: () => void;
}>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  isAdmin: false,
  setIsAuthenticated: () => {},
  checkAuth: () => {}
});

export const AuthProvider = ({ children }: IAuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<null | boolean>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [user, setUser] = React.useState<{
    id: number;
    email: string;
    displayName: string | null;
    avatar: string | null;
    hasActiveSubscription: boolean;
    subscriptionTier: string | null;
    subscriptionExpiresAt: string | null;
    subscriptionCancelledAt: string | null;
    isProGifted: boolean;
    hasFreeTrialAbility: boolean;
    isPro: boolean;
    mfaEnabled: boolean;
    mfaEmailEnabled: boolean;
    mfaPushEnabled: boolean;
    isEnterprise: boolean;
    companyId: number | null;
    companySlug: string | null;
    companyName: string | null;
    companyRole: string | null;
  } | null>(null);
  const [isAdmin, setIsAdmin] = React.useState(false);

  const { mutateAsync: checkMe } = useMutation({
    mutationFn: () => authService.getMe(),
    onSuccess: async (result) => {
      if (result?.user?.email) {
        setIsAuthenticated(true);
        setUser({
          id: result.user.id,
          email: result.user.email,
          displayName: result.user.displayName || null,
          avatar: result.user.avatar || null,
          hasActiveSubscription: result.user.hasActiveSubscription,
          subscriptionTier: result.user.subscriptionTier,
          subscriptionExpiresAt: result.user.subscriptionExpiresAt || null,
          subscriptionCancelledAt: result.user.subscriptionCancelledAt || null,
          isProGifted: result.user.isProGifted || false,
          hasFreeTrialAbility: result.user.hasFreeTrialAbility,
          isPro: result.user.isPro,
          mfaEnabled: result.user.mfaEnabled || false,
          mfaEmailEnabled: result.user.mfaEmailEnabled || false,
          mfaPushEnabled: result.user.mfaPushEnabled || false,
          isEnterprise: result.user.isEnterprise || false,
          companyId: result.user.companyId || null,
          companySlug: result.user.companySlug || null,
          companyName: result.user.companyName || null,
          companyRole: result.user.companyRole || null
        });
        setIsAdmin(result.user.isAdmin === true);

        // Auto-redeem pending Pro invite if one exists
        const pendingToken = getPendingInvite();
        if (pendingToken && !result.user.isProGifted && !result.user.isPro) {
          try {
            const http = new HttpFactoryService().createHttpService();
            await http.post(BACKEND_KEYS.REDEEM_INVITE, { token: pendingToken });
            clearPendingInvite();
            // Re-check auth to get updated Pro status
            const updated = await authService.getMe();
            if (updated?.user) {
              setUser((prev) => prev ? {
                ...prev,
                isProGifted: updated.user.isProGifted || false,
                isPro: updated.user.isPro
              } : prev);
            }
          } catch {
            clearPendingInvite();
          }
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setIsAdmin(false);
      }
      setIsLoading(false);
    },
    onError: () => {
      setIsAuthenticated(false);
      setUser(null);
      setIsAdmin(false);
      setIsLoading(false);
    }
  });
  const throttledCheckMe = useThrottle(checkMe);

  async function checkAuth() {
    setIsLoading(true);
    try {
      await throttledCheckMe();
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }

  React.useEffect(() => {
    checkAuth();
  }, []);

  // Listen for session invalidation via WebSocket
  React.useEffect(() => {
    if (!user?.id) return;

    const backendUrl = (
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_BACKEND_URL_DEV ||
      'http://localhost:4200'
    ).replace(/\/api\/v1\/?$/, '');

    const socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000
    });

    socket.on('connect', () => {
      socket.emit('join-user', user.id);
    });

    socket.on('user:session-invalidated', () => {
      clearStoredToken();
      setIsAuthenticated(false);
      setUser(null);
      setIsAdmin(false);
      window.location.href = ROUTER_LINKS.SIGNIN;
    });

    socket.on('user:subscription-updated', () => {
      checkMe();
    });

    socket.on('reconnect', () => {
      socket.emit('join-user', user.id);
    });

    return () => {
      socket.emit('leave-user', user.id);
      socket.disconnect();
    };
  }, [user?.id]);

  const setIsAuthenticatedWithUser = React.useCallback((value: null | boolean) => {
    setIsAuthenticated(value);
    if (!value) {
      setUser(null);
      setIsAdmin(false);
    }
  }, []);

  const value = React.useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      isAdmin,
      setIsAuthenticated: setIsAuthenticatedWithUser,
      checkAuth
    }),
    [isAuthenticated, setIsAuthenticatedWithUser, isLoading, user, isAdmin]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

const useAuth = () => {
  const context = React.useContext(Context);
  if (!context) {
    throw new Error('useAuth() can only be used within AuthProvider');
  }
  return context;
};

export default useAuth;
